import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AlertService } from 'app/services/alert.service';
import { LoggerService } from 'app/services/logger.service';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { ExpenseStatusService } from '../../services/expense-status.service';
import * as ExpenseStatusActions from './expense-status.actions';

@Injectable()
export class ExpenseStatusEffects {
    private _actions$ = inject(Actions);
    private _store = inject(Store);
    private logger = inject(LoggerService);
    private _ExpenseStatusService = inject(ExpenseStatusService);
    private _alertService = inject(AlertService);

    messages = {
        getAllError: 'Error getting all expensestatuses',
        connectionError: 'Error connecting to the server',
        unknownError: 'Unknown error',
    };

    // -----------------------------------------------------------
    // @ Effects
    // -----------------------------------------------------------

    // Get All Expense Status
    getAllExpenseStatuss$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ExpenseStatusActions.loadAllExpenseStatus),
            switchMap(() => {
                return this._ExpenseStatusService.getAll().pipe(
                    map((res) => {
                        return ExpenseStatusActions.loadAllExpenseStatusSuccess(
                            {
                                status: res.data,
                            }
                        );
                    }),
                    catchError((error) =>
                        of(
                            ExpenseStatusActions.loadAllExpenseStatusFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Get One ExpenseStatus
    getExpenseStatus$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ExpenseStatusActions.loadExpenseStatus),
            switchMap((action) => {
                return this._ExpenseStatusService.getOne(action.id).pipe(
                    map((res) => {
                        return ExpenseStatusActions.loadExpenseStatusSuccess({
                            status: res.data,
                        });
                    }),
                    catchError((error) =>
                        of(
                            ExpenseStatusActions.loadExpenseStatusFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Create ExpenseStatus
    createExpenseStatus$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ExpenseStatusActions.createExpenseStatus),
            switchMap((action) => {
                return this._ExpenseStatusService.create(action.data).pipe(
                    map((res) => {
                        return ExpenseStatusActions.createExpenseStatusSuccess({
                            status: res.data,
                            success: 'Expense Status created successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            ExpenseStatusActions.createExpenseStatusFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Update ExpenseStatus
    updateExpenseStatus$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ExpenseStatusActions.updateExpenseStatus),
            switchMap((action) => {
                return this._ExpenseStatusService
                    .update(action.data, action.id)
                    .pipe(
                        map((res) => {
                            return ExpenseStatusActions.updateExpenseStatusSuccess(
                                {
                                    status: res.data,
                                    success:
                                        'Expense Status updated successfully',
                                }
                            );
                        }),
                        catchError((error) =>
                            of(
                                ExpenseStatusActions.updateExpenseStatusFailure(
                                    {
                                        error:
                                            error?.error?.message ||
                                            this.messages.unknownError,
                                    }
                                )
                            )
                        )
                    );
            })
        )
    );

    // Delete ExpenseStatus
    deleteExpenseStatus$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ExpenseStatusActions.deleteExpenseStatus),
            switchMap((action) => {
                return this._ExpenseStatusService.delete(action.id).pipe(
                    map(() => {
                        return ExpenseStatusActions.deleteExpenseStatusSuccess({
                            id: action.id,
                            success: 'Expense Status deleted successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            ExpenseStatusActions.deleteExpenseStatusFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // -----------------------------------------------------------
    // @ Alerts
    // -----------------------------------------------------------

    onExpenseStatusActionSuccess$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    ExpenseStatusActions.createExpenseStatusSuccess,
                    ExpenseStatusActions.updateExpenseStatusSuccess,
                    ExpenseStatusActions.deleteExpenseStatusSuccess
                ),
                tap((action) => {
                    this._alertService.showAlert(action.success, 'success');
                })
            ),
        { dispatch: false }
    );

    onExpenseStatusActionFailure$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    ExpenseStatusActions.loadAllExpenseStatusFailure,
                    ExpenseStatusActions.loadExpenseStatusFailure,
                    ExpenseStatusActions.createExpenseStatusFailure,
                    ExpenseStatusActions.updateExpenseStatusFailure,
                    ExpenseStatusActions.deleteExpenseStatusFailure
                ),
                tap((action) => {
                    this._alertService.showAlert(action.error, 'error');
                })
            ),
        { dispatch: false }
    );
}
